import {
  AIModelChoices,
  CancellationReasonEnum,
  type ErrorType,
  getApiInspectionsV1CompaniesListQueryKey,
  type Inspection,
  InspectionResult,
  InspectionStatus,
  InspectionType,
  isApiFormError,
  isFieldErrorResponse,
  useApiInspectionsV1Create,
  useApiInspectionsV1Update,
  useApiTradeV1SiteMaterialsRetrieve,
  useQueryClient,
} from 'api';
import { TradeCompanySiteMaterialPicker, useNotification } from 'features';
import {
  CheckboxField,
  DateTimeField,
  Form,
  type SchemaInput,
  SelectField,
  TextField,
  useForm,
  useWatch,
  yup,
  yupResolver,
} from 'form';
import { useTranslation } from 'i18n';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionRow, Alert, Button, Divider, Tooltip, Typography } from 'ui';

import { useAllInspectionCompanies } from '../../../inspection-company';
import { getCompanyOptions } from '../../utils/getCompanyOptions/getCompanyOptions';
import { getInspectorOptionsByInspectionTime } from '../../utils/getInspectorOptions/getInspectorOptionsByInspectionTime';
import { isCompanyActive } from '../../utils/isCompanyActive/isCompanyActive';
import { matchInspectorToCompany } from '../../utils/matchInspectorToCompany/matchInspectorToCompany';
import { InspectionLink } from '../InspectionLink/InspectionLink';
import { CANCELLATION_REASONS, INSPECTION_RESULTS, INSPECTION_STATUSES, INSPECTION_TYPE } from './options';

export type Props = {
  inspection?: Inspection;
  dealId?: number;
  variant: 'create' | 'update';
};

export const InspectionMutate = ({ inspection, dealId, variant }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation('orders');
  const { data: companyData } = useAllInspectionCompanies();

  const [selectedInspectionTime, setSelectedInspectionTime] = useState<Date>(
    inspection?.time ? new Date(inspection.time) : new Date()
  );

  const companyOptions = useMemo(() => {
    if (!companyData) {
      return [];
    }
    return getCompanyOptions(companyData?.results);
  }, [companyData]);

  const inspectorOptions = useMemo(() => {
    if (!companyData) {
      return [];
    }
    return getInspectorOptionsByInspectionTime(companyData?.results, selectedInspectionTime);
  }, [companyData, selectedInspectionTime]);

  const schema = useMemo(() => {
    return yup.object().shape(
      {
        cancellation_reason: yup
          .string()
          .oneOf(Object.values(CancellationReasonEnum))
          .nullable()
          .when('status', {
            is: (status: string) => {
              return status === 'CA';
            },
            otherwise: () => yup.string().nullable().notRequired(),
            then: () =>
              yup.string().required(t('inspectionMutate.validation.cancellationReasonRequiredForCancelledInspections')),
          }),
        deal: yup.number().nullable().notRequired(),
        external_report_link: yup
          .string()
          .url(t('inspectionMutate.validation.linkMustBeValidUrl'))
          .nullable()
          .notRequired(),
        inspected_correctly: yup.string().oneOf(['yes', 'no']).notRequired(),
        inspection_company: yup
          .number()
          .nullable()
          .notRequired()
          .test({
            message: t('inspectionMutate.validation.inspectionCompanyMustBeActive'),
            name: 'company-active',
            test: isCompanyActive(companyData?.results),
          }),
        inspection_result: yup.string().oneOf(Object.values(InspectionResult)).required(),
        inspector: yup
          .number()
          .nullable()
          .notRequired()
          .test(
            'inspector-company',
            t('inspectionMutate.validation.inspectorMustBelongToSelectedCompany'),
            matchInspectorToCompany(inspectorOptions, companyOptions)
          ),
        invalid_inspection_reason: yup.string().optional(),
        is_ai_gravi_sample_enabled: yup.boolean(),
        model: yup.string().nullable().notRequired(),
        notes: yup.string().nullable().notRequired(),
        site_material: yup
          .number()
          .required(t('inspectionMutate.validation.siteMaterialRequired'))
          .transform((value) => (value === Number.POSITIVE_INFINITY ? null : value)),
        status: yup.string().oneOf(Object.values(InspectionStatus)).required(),
        time: yup.string().required(),
        trade_company: yup.number().notRequired().optional(),
        type: yup.string().oneOf(Object.values(InspectionType)).required(),
      },
      [['inspection_company', 'inspector']]
    );
  }, [companyData, companyOptions, inspectorOptions, t]);

  type FormValues = yup.InferType<typeof schema>;
  type FormInputValues = SchemaInput<typeof schema>;
  const { open } = useNotification();

  const form = useForm<FormInputValues, unknown, FormValues>({
    defaultValues: {
      status: 'BL',
      ...inspection,
      cancellation_reason: inspection?.cancellation_reason || null,
      deal: dealId || null,
      inspection_result: inspection?.inspection_result || InspectionResult.PENDING,
      invalid_inspection_reason: inspection?.invalid_inspection_reason || '',
      is_ai_gravi_sample_enabled: Boolean(inspection?.is_ai_gravi_sample_enabled),
      site_material: inspection?.site_material ?? undefined,
      time: inspection?.time ?? undefined,
    },
    resolver: yupResolver(schema),
  });

  const { setValue, control } = form;
  const queryClient = useQueryClient();

  const {
    inspected_correctly,
    status,
    site_material: selectedSiteMaterial,
    is_ai_gravi_sample_enabled,
  } = useWatch({ control });
  const { data: siteMaterialData } = useApiTradeV1SiteMaterialsRetrieve(String(selectedSiteMaterial), {
    query: {
      enabled: Boolean(selectedSiteMaterial),
    },
  });

  useEffect(() => {
    setValue('deal', dealId || null);
  }, [dealId, setValue]);

  useEffect(() => {
    if (siteMaterialData) {
      setValue('trade_company', siteMaterialData?.site?.trade_company.id);
    }
  }, [setValue, siteMaterialData]);

  const handleTimeChange = (value: string | null) => {
    if (!value) {
      return;
    }
    const date = new Date(value);
    setSelectedInspectionTime(date);
  };

  const handleSuccess = () => {
    open({
      message: t('inspectionMutate.notifications.changesSaved'),
      type: 'success',
    });
    queryClient.invalidateQueries({
      queryKey: getApiInspectionsV1CompaniesListQueryKey(),
    });
  };

  const handleError = (error: ErrorType<unknown>) => {
    if (isApiFormError(error)) {
      open({
        description: error?.response?.data.detail,
        message: t('inspectionMutate.notifications.errorMutatingInspection'),
        type: 'error',
      });
      return;
    }
    if (isFieldErrorResponse(error)) {
      Object.keys(error.response?.data).forEach((field) => {
        form.setError(field as keyof FormValues, { message: error.response.data[field] });
      });
      if (error.response?.data.non_field_errors) {
        open({
          description: error?.response?.data.non_field_errors[0],
          message: t('inspectionMutate.notifications.errorMutatingInspection'),
          type: 'error',
        });
      }
      return;
    }

    open({
      message: t('inspectionMutate.notifications.errorMutatingInspection'),
      type: 'error',
    });
  };

  const { mutate: create, isPending: isCreatePending } = useApiInspectionsV1Create({
    mutation: {
      onError: handleError,
      onSuccess: (data) => {
        handleSuccess();
        navigate(`/ground-control/inspections/${data.id}`);
      },
    },
  });

  const { mutate: edit, isPending: isEditPending } = useApiInspectionsV1Update({
    mutation: {
      onError: handleError,
      onSuccess: handleSuccess,
    },
  });

  const handleSubmit = (values: FormValues) => {
    if (inspection) {
      edit({
        data: values,
        id: inspection.id,
      });
      return;
    }
    create({
      data: values,
    });
  };

  const aiSampleEnabled = useMemo(() => {
    if (!selectedSiteMaterial || !siteMaterialData) {
      return false;
    }
    return (
      siteMaterialData.material_spec?.ai_model === AIModelChoices.alu ||
      siteMaterialData.material_spec?.ai_model === AIModelChoices.briquette ||
      siteMaterialData.material_spec?.ai_model === AIModelChoices.pet ||
      siteMaterialData.material_spec?.ai_model === AIModelChoices.pet_envases
    );
  }, [selectedSiteMaterial, siteMaterialData]);

  const is_ai_model_configured_on_material = useMemo(() => {
    if (!selectedSiteMaterial || !siteMaterialData) {
      return false;
    }
    return Boolean(siteMaterialData.material_spec?.ai_model);
  }, [selectedSiteMaterial, siteMaterialData]);

  const aiSampleCheckboxText = useMemo(() => {
    if (!selectedSiteMaterial) {
      return t('inspectionMutate.aiSample.tooltips.siteMaterialRequiredToEnableSamples');
    }
    if (!aiSampleEnabled) {
      return t('inspectionMutate.aiSample.tooltips.aiLooseSampleNotAvailableForMaterial');
    }
    return '';
  }, [selectedSiteMaterial, aiSampleEnabled, t]);

  useEffect(() => {
    if (!aiSampleEnabled) {
      setValue('is_ai_gravi_sample_enabled', false);
    } else {
      setValue('is_ai_gravi_sample_enabled', Boolean(inspection?.is_ai_gravi_sample_enabled));
    }
  }, [aiSampleEnabled, inspection?.is_ai_gravi_sample_enabled, setValue]);

  return (
    <Form<FormInputValues, unknown, FormValues> {...form} onSubmit={handleSubmit}>
      <Typography variant="h6">
        {inspection
          ? t('inspectionMutate.title.editInspection', { values: { slug: inspection.slug } })
          : t('inspectionMutate.title.createInspection')}
      </Typography>
      {form.formState.errors.deal ? <Alert severity="error">{String(form.formState.errors.deal.message)}</Alert> : null}
      <TradeCompanySiteMaterialPicker
        slotProps={{
          siteMaterial: {
            label: t('inspectionMutate.labels.siteMaterial'),
            name: 'site_material',
          },
          tradeCompany: {
            defaultValue: siteMaterialData?.site?.trade_company.id,
            name: 'trade_company',
          },
        }}
      />
      <Divider />
      <DateTimeField
        required
        format="DD/MM/YYYY HH:mm"
        ampm={false}
        label={t('inspectionMutate.labels.inspectionTime')}
        name="time"
        onChange={handleTimeChange}
      />
      <SelectField
        required
        name="status"
        id="inspection-status"
        label={t('inspectionMutate.labels.status')}
        options={INSPECTION_STATUSES}
      />
      <SelectField
        name="cancellation_reason"
        label={t('inspectionMutate.labels.cancellationReasonIfApplicable')}
        disabled={status !== 'CA'}
        options={CANCELLATION_REASONS}
      />
      <SelectField
        required
        name="type"
        label={t('inspectionMutate.labels.type')}
        options={INSPECTION_TYPE}
        data-testid="inspection-type"
        inputProps={{
          'data-testid': 'inspection-type-input',
        }}
      />
      <SelectField
        required={false}
        name="inspection_company"
        data-testid="inspection-company"
        label={t('inspectionMutate.labels.company')}
        disabled={companyOptions.length === 0}
        options={companyOptions}
        inputProps={{
          'data-testid': 'inspection-company-input',
        }}
      />
      <SelectField
        required={false}
        name="inspector"
        label={t('inspectionMutate.labels.inspector')}
        disabled={inspectorOptions.length === 0}
        options={inspectorOptions}
      />
      <TextField multiline name="notes" label={t('inspectionMutate.labels.notes')} />
      <TextField name="external_report_link" label={t('inspectionMutate.labels.linkToExternalReport')} />
      <Tooltip title={aiSampleCheckboxText}>
        <div>
          <CheckboxField
            name="is_ai_gravi_sample_enabled"
            label={t('inspectionMutate.labels.enableAiLooseSample')}
            disabled={!aiSampleEnabled}
          />
        </div>
      </Tooltip>
      {variant === 'update' && (
        <>
          <Divider />
          <SelectField
            name="inspection_result"
            label={t('inspectionMutate.labels.inspectionResult')}
            options={INSPECTION_RESULTS}
          />
          <SelectField
            name="inspected_correctly"
            label={t('inspectionMutate.labels.inspectedCorrectly')}
            options={[
              {
                label: t('inspectionMutate.values.notSpecified'),
                value: undefined,
              },
              { label: t('inspectionMutate.values.yes'), value: 'yes' },
              { label: t('inspectionMutate.values.no'), value: 'no' },
            ]}
          />
          {inspected_correctly === 'no' ? (
            <TextField
              multiline
              name="invalid_inspection_reason"
              label={t('inspectionMutate.labels.invalidInspectionReasonIfApplicable')}
              minRows={2}
            />
          ) : null}
        </>
      )}
      <ActionRow>
        <InspectionLink
          inspectionId={inspection?.id}
          disabled={!is_ai_model_configured_on_material && !is_ai_gravi_sample_enabled}
        />
        <Button fullWidth type="submit" variant="primary" loading={isCreatePending || isEditPending}>
          {t('inspectionMutate.actions.save')}
        </Button>
      </ActionRow>
    </Form>
  );
};
